import { IQueryMany, IQueryOne } from "../interfaces";
import { serializeFilters } from "../utils";

export const QUERIES = {
  LOCATION: {
    getLatestCollections(): IQueryMany {
      return {
        select: "slug,name,id,organization,type,updatedAt",
        filter: `type==Collection`,
        page: { size: 5 },
        sort: "-updatedAt",
      };
    },

    getFiltered(search: string, filters): IQueryMany {
      return {
        search,
        filter: serializeFilters(filters),
        select: "name,slug,organization,type",
        include: "parentPlaces",
        sort: "name",
      };
    },

    getAllFiltered(search: string, query?: { [key: string]: any }): IQueryMany {
      return {
        search,
        sort: "name",
        select: "name,slug,organization,type",
        ...query,
      };
    },

    getExceptCollectionForOrg(
      search: string,
      org: string,
      includePublic: boolean = false
    ): IQueryMany {
      return {
        search,
        filter: `type!=Collection`,
        select: "name,slug,organization,type",
        sort: "name",
        group: org,
        public: includePublic,
      };
    },

    getFeatured(): IQueryMany {
      return {
        select: "slug,name,id,organization,type",
        page: { size: 100 },
        filter: "featured==true",
        sort: "name",
      };
    },

    getCollection(group: string): IQueryOne {
      return {
        include: "locations,metrics",
        select: "locations.slug,locations.name",
        group,
      };
    },

    getOne(group): IQueryOne {
      return {
        select: "id,slug,name,organization,type,geojson,bbox2d,fitBbox2d",
        group,
      };
    },

    getOneWithoutGeoJSON(group): IQueryOne {
      return {
        include: "metrics,associatedPlaces",
        select: "-geojson",
        group,
      };
    },
  },

  DASHBOARD: {
    getAllFiltered(search: string, query?: { [key: string]: any }): IQueryMany {
      return {
        search,
        sort: "name",
        select: "name,slug,published,updatedAt",
        include: "widgets",
        ...query,
      };
    },
    getWithWidgets(): IQueryOne {
      return {
        include: [
          "widgets",
          "widgets.layers",
          "widgets.layers.references",
          "widgets.metrics",
        ].join(","),
      };
    },
  },

  LAYER: {
    getActive(slugs: string): IQueryMany {
      if (!slugs) {
        // prevent SWR from fetching
        return null;
      }

      const query: IQueryMany = {
        filter: serializeFilters({
          slug: slugs,
          showShared: true,
        }),
        // Nested references support groups of groups; parent carries the
        // ancestor-group metadata chain for child layers.
        include:
          "references,references.references,references.references.references,parent",
      };

      return query;
    },

    getPrimaryFiltered(
      search: string,
      filters,
      options: { sort?: string; pageSize?: number; facets?: boolean } = {}
    ): IQueryMany {
      const { sort, pageSize, facets } = options;
      // "relevance" means "let the server rank by search match quality", which
      // it does when no sort token is sent. Any other sort (name, createdAt) is
      // forwarded — including during a search, so the user can override relevance.
      const sortToken = sort && sort !== "relevance" ? sort : undefined;
      return {
        search,
        ...(sortToken ? { sort: sortToken } : {}),
        filter: serializeFilters({
          ...filters,
          primary: true,
        }),
        include:
          "references,references.references,references.references.references",
        ...(pageSize ? { page: { size: pageSize } } : {}),
        // Opt out of facet computation when the caller won't render them.
        ...(facets === false ? { facets: false } : {}),
      };
    },

    getAllFiltered(
      search: string,
      query?: { [key: string]: any },
      select?: string
    ): IQueryMany {
      return {
        search,
        sort: "name",
        select:
          "name,slug,tags,primary,category,published,available,accessibleWithLink,provider,type,description,readMoreLinks,downloadLinks,sources,citation,license,organization,createdAt,updatedAt" +
          (select ? `,${select}` : ""),
        include: "references",
        ...query,
      };
    },

    getGroupReferenceCandidates(search: string, excludeId: string, includeGroups: boolean = true): IQueryMany {
      const conditions = [
        // The layer being edited is excluded so a group can't reference
        // itself (the API also rejects deeper reference cycles on save).
        ...(excludeId ? [`_id!=${excludeId}`] : []),
        ...(includeGroups ? [] : ["type!=group"]),
      ];
      return {
        search: search,
        ...(!search && { sort: "name" }),
        ...(conditions.length ? { filter: conditions.join(",") } : {}),
        select: "name,slug,type,config.legendConfig",
      };
    },

    getForExport(search: string): IQueryMany {
      return {
        search,
        filter: serializeFilters({ exportable: true }),
        include: "references",
        select:
          "name,organization,config,references.name,references.config,references.licenseRestriction,references.organization,references.provider",
        sort: "name",
      };
    },
  },

  ANALYSIS: {
    getAll(): IQueryMany {
      return {};
    },
    getForAnalysisId(analysisId): IQueryMany {
      return {
        filter: analysisId ? `analysisId==${analysisId}` : "",
        sort: "-createdAt",
        page: { size: 100 },
        select:
          "slug,analysisId,name,id,locationId,organization,type,status,settings,startedAt,endedAt,task,result,createdAt,updatedAt,config",
      };
    },
    getForAnalysisIds(analysisIds: string[]): IQueryMany {
      return {
        filter: serializeFilters({
          analysisId: analysisIds,
          status: "COMPLETE",
        }),
        sort: "-createdAt",
        select:
          "slug,analysisId,name,id,locationId,organization,type,status,settings,task,result,startedAt,endedAt,createdAt,updatedAt,config",
      };
    },
    getAllFiltered(group, type = ""): IQueryMany {
      return {
        group,
        filter: type ? `type==${type}` : "",
        sort: "id",
        select:
          "id,name,slug,locationId,location,config,type,organization,description",
      };
    },
    getOne(group): IQueryOne {
      return {
        group,
      };
    },
    getLatest(type = ""): IQueryMany {
      return {
        select:
          "slug,analysisId,name,id,locationId,organization,type,status,settings,startedAt,endedAt,task,result,createdAt,updatedAt,config",
        include: "location",
        filter: type ? `type==${type}` : "",
        sort: "-updatedAt",
      };
    },
  },

  WIDGET: {
    getAllFiltered(
      search?: string,
      query?: { [key: string]: any }
    ): IQueryMany {
      return {
        search,
        sort: "id,name",
        select:
          "id,name,slug,widgetType,widgetChart,published,layers,metrics,updatedAt",
        include: "layers,metrics",
        ...query,
      };
    },
  },

  WORKSPACE: {
    getAll(): IQueryMany {
      return {
        sort: "id,name",
        select: "id,name,slug",
      };
    },

    getOne(): IQueryOne {
      return {
        include: "owners",
      };
    },
  },

  USER: {
    getAll() {
      return {
        include: "groups",
      };
    },
  },

  ZONAL_METRICS: {
    getAll(): IQueryMany {
      return {
        sort: "name",
        select: "id,name,slug,categorical",
      };
    },
    getAllFiltered(
      search: string = "",
      query?: { [key: string]: any }
    ): IQueryMany {
      return {
        search,
        sort: "name",
        select:
          "id,name,slug,method,categorical,published,updatedAt,resources,widgets,calculateForPlaceTypes",
        include: "resources,widgets",
        ...query,
      };
    },
  },
};
